import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, Play, ClipboardList, BookOpen, Globe, Download, Search, Inbox, Gamepad2, GraduationCap, SquareStack, FolderPlus, Library } from "lucide-react";
import { Deck, Folder, LANG_FLAGS, LANG_NAMES } from "@/types/linguadeck";
import { triggerBackgroundTtsForCards } from "@/lib/triggerBackgroundTts";
import {
  fetchGlobalLibraryPage,
  createDeck,
  fetchGlobalDeckForStudy,
  fetchDecksLight,
  fetchFolders,
  addDecksToFolder,
  DeckAlreadyImportedError,
  fetchUserDeckIdsBySourceDeckIds,
  fetchStudentDeckBySourceId,
} from "@/lib/db";
import { inspectQueryError, validateWriteReadConsistency, shouldValidateReadAfterWrite } from "@/diagnostics";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { filterToLaunchMode, getDeckLaunchLabel, type DeckLaunchMode } from "@/lib/deckLaunch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

type StudyModeFilter = "cards" | "learn" | "test" | "match" | "philology";
type TimeGroup = "today" | "this_week" | "older";
type SortOption = "recent" | "alphabetical" | "language";
type DateFilterOption = "all" | "today";

function mergeUniqueDecks(existing: Deck[], incoming: Deck[]): Deck[] {
  if (incoming.length === 0) return existing;
  const merged = [...existing];
  const indexById = new Map(existing.map((d, i) => [d.id, i]));
  for (const d of incoming) {
    if (indexById.has(d.id)) merged[indexById.get(d.id)!] = d;
    else {
      indexById.set(d.id, merged.length);
      merged.push(d);
    }
  }
  return merged;
}

function getTimeGroup(iso: string | undefined): TimeGroup {
  if (!iso) return "older";
  const date = new Date(iso);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = (todayStart - dateStart) / (24 * 60 * 60 * 1000);
  if (diffDays === 0) return "today";
  if (diffDays <= 7) return "this_week";
  return "older";
}

const PAGE_SIZE = 60;

export default function GlobalLibraryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [decks, setDecks] = useState<Deck[]>([]);
  const [totalDecks, setTotalDecks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [savingDeckId, setSavingDeckId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeLanguage, setActiveLanguage] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>("recent");
  const [dateFilter, setDateFilter] = useState<DateFilterOption>("all");
  const [activeTypeFilter, setActiveTypeFilter] = useState<StudyModeFilter | null>(null);
  const [launchingDeckId, setLaunchingDeckId] = useState<string | null>(null);
  const [addAllToLibraryLoading, setAddAllToLibraryLoading] = useState(false);
  const [addAllToLibraryProgress, setAddAllToLibraryProgress] = useState<{ current: number; total: number } | null>(null);
  const [addToFolderModalOpen, setAddToFolderModalOpen] = useState(false);
  const [foldersForModal, setFoldersForModal] = useState<Folder[]>([]);
  const [selectedFolderIdForBulk, setSelectedFolderIdForBulk] = useState("");
  const [addToFolderLoading, setAddToFolderLoading] = useState(false);
  const [addToFolderProgress, setAddToFolderProgress] = useState<{ phase: "fetching" | "copying" | "adding"; current: number; total: number } | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);
  const requestVersionRef = useRef(0);
  const mobileAutoLoadCountRef = useRef(0);
  const MOBILE_AUTO_LOAD_MAX = 5;
  const loadingRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const decksRef = useRef<Deck[]>([]);
  const totalDecksRef = useRef(0);

  const hasMoreDecks = decks.length < totalDecks;

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(searchQuery), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);
  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);
  useEffect(() => {
    decksRef.current = decks;
  }, [decks]);
  useEffect(() => {
    totalDecksRef.current = totalDecks;
  }, [totalDecks]);

  const loadMore = useCallback(async () => {
    if (!user || loadingRef.current || loadingMoreRef.current || decksRef.current.length >= totalDecksRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const req = requestVersionRef.current;
    try {
      const { items, total } = await fetchGlobalLibraryPage({
        limit: PAGE_SIZE,
        offset: decksRef.current.length,
        search: searchDebounced.trim() || undefined,
        language: activeLanguage ?? undefined,
        sort: sortBy,
        typeFilter: activeTypeFilter ?? undefined,
        dateFilter: dateFilter === "today" ? "today" : undefined,
      });
      if (req !== requestVersionRef.current) return;
      if (items.length === 0) {
        setTotalDecks(decksRef.current.length);
        totalDecksRef.current = decksRef.current.length;
      } else {
        setDecks((prev) => {
          const merged = mergeUniqueDecks(prev, items);
          decksRef.current = merged;
          return merged;
        });
        setTotalDecks(total);
        totalDecksRef.current = total;
      }
    } catch (e) {
      inspectQueryError({
        queryName: "fetchGlobalLibraryPage",
        flow: "globalLibrary.loadMore",
        view: "deck_summaries",
        pagination: { limit: PAGE_SIZE, offset: decksRef.current.length },
        responseError: e,
      });
      console.error("Global library load more failed:", e);
    } finally {
      if (req === requestVersionRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [user, searchDebounced, activeLanguage, sortBy, activeTypeFilter, dateFilter]);

  // При фильтре «Сегодня» автоматически подгружать остальные страницы (и на ПК, и на мобильном), чтобы показывать все наборы за сегодня.
  useEffect(() => {
    if (dateFilter !== "today") {
      mobileAutoLoadCountRef.current = 0;
      return;
    }
    if (!hasMoreDecks || loading || loadingMore || decks.length === 0 || mobileAutoLoadCountRef.current >= MOBILE_AUTO_LOAD_MAX) return;
    const timer = setTimeout(() => {
      mobileAutoLoadCountRef.current += 1;
      void loadMore();
    }, 350);
    return () => clearTimeout(timer);
  }, [dateFilter, hasMoreDecks, loading, loadingMore, decks.length, loadMore]);

  useEffect(() => {
    requestVersionRef.current += 1;
    const req = requestVersionRef.current;
    if (!user) {
      setDecks([]);
      setTotalDecks(0);
      setLoading(false);
      loadingRef.current = false;
      return;
    }
    loadingRef.current = true;
    loadingMoreRef.current = false;
    decksRef.current = [];
    totalDecksRef.current = 0;
    setLoading(true);
    setLoadingMore(false);
    setDecks([]);
    setTotalDecks(0);

    fetchGlobalLibraryPage({
      limit: PAGE_SIZE,
      offset: 0,
      search: searchDebounced.trim() || undefined,
      language: activeLanguage ?? undefined,
      sort: sortBy,
      typeFilter: activeTypeFilter ?? undefined,
      dateFilter: dateFilter === "today" ? "today" : undefined,
    })
      .then(({ items, total }) => {
        if (req !== requestVersionRef.current) return;
        const deduped = mergeUniqueDecks([], items);
        decksRef.current = deduped;
        totalDecksRef.current = total;
        setDecks(deduped);
        setTotalDecks(total);
      })
      .catch((e) => {
        if (req === requestVersionRef.current) {
          inspectQueryError({
            queryName: "fetchGlobalLibraryPage",
            flow: "globalLibrary.load",
            view: "deck_summaries",
            pagination: { limit: PAGE_SIZE, offset: 0 },
            responseError: e,
          });
          console.error("Global library load failed:", e);
          setDecks([]);
          setTotalDecks(0);
        }
      })
      .finally(() => {
        if (req === requestVersionRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      });
  }, [user, searchDebounced, activeLanguage, sortBy, activeTypeFilter, dateFilter]);

  useEffect(() => {
    if (!loadMoreSentinelRef.current || !user || loading || loadingMore || !hasMoreDecks) {
      loadMoreObserverRef.current?.disconnect();
      loadMoreObserverRef.current = null;
      return;
    }
    const sentinel = loadMoreSentinelRef.current;
    let requested = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || requested) return;
        requested = true;
        void loadMore();
      },
      { root: null, rootMargin: "0px 0px 320px 0px", threshold: 0.01 }
    );
    observer.observe(sentinel);
    loadMoreObserverRef.current = observer;
    return () => {
      observer.disconnect();
      if (loadMoreObserverRef.current === observer) loadMoreObserverRef.current = null;
    };
  }, [user, loading, loadingMore, hasMoreDecks, loadMore]);

  const handleLaunchStudy = useCallback(
    async (deck: Deck, mode: DeckLaunchMode, dashboardOnly = false) => {
      setLaunchingDeckId(deck.id);
      try {
        const fullDeck = deck.cards?.length
          ? deck
          : await fetchGlobalDeckForStudy(deck.id);
        if (!fullDeck) return;
        const fullscreenSuffix =
          dashboardOnly
            ? ""
            : mode === "learn"
              ? "?type=quiz"
              : mode === "test"
                ? "?type=test"
                : "";
        navigate(`/study/${deck.id}${fullscreenSuffix}`, {
          state: {
            deck: fullDeck,
            initialTab: mode === "games" ? "games" : mode,
            fromGlobalLibrary: true,
            ...(dashboardOnly ? { showDashboardOnly: true } : {}),
          },
        });
      } catch (e) {
        console.error("Failed to load deck for study:", e);
      } finally {
        setLaunchingDeckId(null);
      }
    },
    [navigate]
  );

  const handleAddToLibrary = async (deck: Deck) => {
    if (!user) {
      navigate("/auth", { state: { returnTo: "/global-library" } });
      return;
    }
    setSavingDeckId(deck.id);
    try {
      let deckToSave = deck;
      if (!deck.cards?.length) {
        const full = await fetchGlobalDeckForStudy(deck.id);
        if (full) deckToSave = full;
      }
      const created = await createDeck(
        user.id,
        {
          title: deckToSave.title,
          lang: deckToSave.lang,
          emoji: deckToSave.emoji,
          level: deckToSave.level,
          deckType: deckToSave.deckType,
          studyModes: deckToSave.studyModes ?? { cards: true, learn: true, test: true, match: true },
          cards: deckToSave.cards,
        },
        { sourceDeckId: deck.id }
      );
      if (created.cards?.length) {
        triggerBackgroundTtsForCards(created.cards, created.lang ?? "en");
      }
      if (shouldValidateReadAfterWrite("library.addDeckToLibrary")) {
        validateWriteReadConsistency({
          flow: "library.addDeckToLibrary",
          entity: "deck",
          entityId: created.id,
          userId: user.id,
          writeSucceeded: true,
          expectedPresence: true,
          fetchValidator: () => fetchDecksLight(user.id),
          checkPresence: (list) => Array.isArray(list) && list.some((d) => d.id === created.id),
        }).catch(() => {});
      }
      toast.success(t("globalLibrary.addedToLibrary"));
    } catch (err) {
      if (err instanceof DeckAlreadyImportedError) {
        toast.info(t("shared.deckAlreadyInLibrary"));
      } else {
        inspectQueryError({
          queryName: "createDeck",
          flow: "globalLibrary.addToLibrary",
          table: "decks",
          responseError: err,
        });
        console.error("Failed to add deck:", err);
      }
    } finally {
      setSavingDeckId(null);
    }
  };

  const handleAddAllTodayToLibrary = useCallback(async () => {
    if (!user || addAllToLibraryLoading || decks.length === 0) return;
    setAddAllToLibraryLoading(true);
    setAddAllToLibraryProgress({ current: 0, total: decks.length });
    let added = 0;
    let skipped = 0;
    const progressStep = Math.max(1, Math.floor(decks.length / 50));
    try {
      let index = 0;
      for (const deck of decks) {
        if (index % progressStep === 0 || index === decks.length - 1) {
          setAddAllToLibraryProgress({ current: index + 1, total: decks.length });
        }
        index++;
        try {
          let deckToSave = deck;
          if (!deckToSave.cards?.length) {
            const full = await fetchGlobalDeckForStudy(deck.id);
            if (full) deckToSave = full;
          }
          await createDeck(
            user.id,
            {
              title: deckToSave.title,
              lang: deckToSave.lang,
              emoji: deckToSave.emoji,
              level: deckToSave.level,
              deckType: deckToSave.deckType,
              studyModes: deckToSave.studyModes ?? { cards: true, learn: true, test: true, match: true },
              cards: deckToSave.cards ?? [],
            },
            { sourceDeckId: deck.id }
          );
          added++;
        } catch (err) {
          if (err instanceof DeckAlreadyImportedError) skipped++;
          else {
            inspectQueryError({
              queryName: "createDeck",
              flow: "globalLibrary.addAllToday",
              table: "decks",
              responseError: err,
            });
          }
        }
      }
      if (added > 0) {
        toast.success(skipped > 0 ? t("globalLibrary.addAllPartial", { added, skipped }) : t("globalLibrary.addAllSuccess", { count: added }));
      } else if (skipped === decks.length) {
        toast.info(t("shared.deckAlreadyInLibrary"));
      }
    } finally {
      setAddAllToLibraryLoading(false);
      setAddAllToLibraryProgress(null);
    }
  }, [user, decks, t]);

  const openAddToFolderModal = useCallback(async () => {
    if (!user) return;
    setAddToFolderModalOpen(true);
    setSelectedFolderIdForBulk("");
    try {
      const list = await fetchFolders(user.id);
      setFoldersForModal(list);
      if (list.length > 0) setSelectedFolderIdForBulk(list[0].id);
    } catch {
      setFoldersForModal([]);
    }
  }, [user]);

  const handleAddToFolderConfirm = useCallback(async () => {
    if (!user || !selectedFolderIdForBulk.trim() || addToFolderLoading) return;
    setAddToFolderLoading(true);
    setAddToFolderProgress({ phase: "fetching", current: 0, total: 0 });
    try {
      let decksList: Deck[];
      if (dateFilter === "today") {
        setAddToFolderProgress({ phase: "fetching", current: 0, total: 1 });
        const allDecks: Deck[] = [];
        let offset = 0;
        let total = 0;
        let first = true;
        while (first || offset < total) {
          const { items, total: nextTotal } = await fetchGlobalLibraryPage({
            limit: PAGE_SIZE,
            offset,
            search: searchDebounced.trim() || undefined,
            language: activeLanguage ?? undefined,
            sort: sortBy,
            typeFilter: activeTypeFilter ?? undefined,
            dateFilter: "today",
          });
          if (first) {
            total = nextTotal;
            first = false;
          }
          if (items.length === 0) break;
          for (const d of items) {
            if (!allDecks.some((x) => x.id === d.id)) allDecks.push(d);
          }
          offset += items.length;
          if (items.length < PAGE_SIZE) break;
        }
        decksList = allDecks;
      } else {
        decksList = [...decksRef.current];
      }
      if (decksList.length === 0) {
        toast.error(t("shared.error"));
        return;
      }
      setAddToFolderProgress({ phase: "copying", current: 0, total: decksList.length });
      const sourceIds = decksList.map((d) => d.id);
      const userDeckIdsBySource = await fetchUserDeckIdsBySourceDeckIds(user.id, sourceIds);
      const userDeckIdsToAdd: string[] = [];
      const progressStep = Math.max(1, Math.floor(decksList.length / 50));
      for (let i = 0; i < decksList.length; i++) {
        if (i % progressStep === 0 || i === decksList.length - 1) {
          setAddToFolderProgress({ phase: "copying", current: i + 1, total: decksList.length });
        }
        const deck = decksList[i];
        const existingId = userDeckIdsBySource.get(deck.id);
        if (existingId) {
          userDeckIdsToAdd.push(existingId);
          continue;
        }
        try {
          let deckToSave = deck;
          if (!deckToSave.cards?.length) {
            const full = await fetchGlobalDeckForStudy(deck.id);
            if (full) deckToSave = full;
          }
          const created = await createDeck(
            user.id,
            {
              title: deckToSave.title,
              lang: deckToSave.lang,
              emoji: deckToSave.emoji,
              level: deckToSave.level,
              deckType: deckToSave.deckType,
              studyModes: deckToSave.studyModes ?? { cards: true, learn: true, test: true, match: true },
              cards: deckToSave.cards ?? [],
            },
            { sourceDeckId: deck.id }
          );
          userDeckIdsToAdd.push(created.id);
        } catch (err) {
          if (err instanceof DeckAlreadyImportedError) {
            const existing = await fetchStudentDeckBySourceId(user.id, deck.id);
            if (existing?.id) userDeckIdsToAdd.push(existing.id);
          } else {
            inspectQueryError({
              queryName: "createDeck",
              flow: "globalLibrary.addToFolder",
              table: "decks",
              responseError: err,
            });
          }
        }
      }
      if (userDeckIdsToAdd.length === 0) {
        toast.error(t("shared.error"));
        return;
      }
      const uniqueDeckIds = [...new Set(userDeckIdsToAdd)];
      setAddToFolderProgress({ phase: "adding", current: uniqueDeckIds.length, total: uniqueDeckIds.length });
      await addDecksToFolder(selectedFolderIdForBulk, uniqueDeckIds);
      toast.success(
        uniqueDeckIds.length === decksList.length
          ? t("globalLibrary.addToFolderSuccess")
          : t("globalLibrary.addToFolderPartial", { count: uniqueDeckIds.length, total: decksList.length })
      );
      setAddToFolderModalOpen(false);
    } catch (e) {
      inspectQueryError({
        queryName: "addDecksToFolder",
        flow: "globalLibrary.addToFolder",
        table: "folder_decks",
        responseError: e,
      });
      toast.error(t("shared.error"));
    } finally {
      setAddToFolderLoading(false);
      setAddToFolderProgress(null);
    }
  }, [user, selectedFolderIdForBulk, dateFilter, addToFolderLoading, t, searchDebounced, activeLanguage, sortBy, activeTypeFilter]);

  const groupedDecks = useMemo(() => {
    const g: Record<TimeGroup, Deck[]> = { today: [], this_week: [], older: [] };
    for (const d of decks) {
      g[getTimeGroup(d.created_at)].push(d);
    }
    return g;
  }, [decks]);

  const uniqueLanguages = useMemo(() => {
    const seen = new Set<string>();
    const out: { lang: string; emoji: string; label: string }[] = [];
    for (const d of decks) {
      if (d.lang && !seen.has(d.lang)) {
        seen.add(d.lang);
        out.push({
          lang: d.lang,
          emoji: LANG_FLAGS[d.lang] ?? d.emoji ?? "📚",
          label: LANG_NAMES[d.lang] ?? `${d.emoji ?? "📚"} ${d.lang}`,
        });
      }
    }
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [decks]);

  const centerStyle: React.CSSProperties = {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "hsl(var(--bg))",
    fontFamily: "DM Sans, sans-serif",
    textAlign: "center",
    padding: 24,
  };

  if (!user) {
    return (
      <div style={centerStyle}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{t("globalLibrary.signInRequired")}</div>
        <p style={{ color: "hsl(var(--muted-foreground))", marginBottom: 24 }}>{t("globalLibrary.signInHint")}</p>
        <Link
          to="/auth"
          state={{ returnTo: "/global-library" }}
          style={{
            padding: "10px 24px",
            borderRadius: 50,
            background: "hsl(var(--primary))",
            color: "white",
            textDecoration: "none",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {t("common.signIn")}
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full max-w-[1200px] mx-auto px-4 py-6 sm:px-6">
        <div className="grid gap-4 md:gap-5 w-full" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(260px, 100%), 1fr))" }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="relative overflow-hidden rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-3.5 md:p-5 shadow-sm">
              <div className="absolute inset-x-0 top-0 h-[2px] rounded-t-xl bg-[hsl(var(--bg3))] animate-pulse" />
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-[hsl(var(--bg3))] animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 rounded bg-[hsl(var(--bg3))] animate-pulse" />
                  <div className="h-3 w-12 rounded bg-[hsl(var(--bg3))] animate-pulse" />
                </div>
              </div>
              <div className="mt-3 h-6 w-20 rounded-lg bg-[hsl(var(--bg3))] animate-pulse" />
              <div className="mt-4 flex flex-col gap-2">
                <div className="h-11 rounded-xl bg-[hsl(var(--bg3))] animate-pulse" />
                <div className="h-10 rounded-xl bg-[hsl(var(--bg3))] animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col overflow-x-hidden w-full max-w-full bg-[hsl(var(--bg))]"
      style={{ fontFamily: "DM Sans, sans-serif" }}
    >
      <div className="w-full max-w-[1200px] mx-auto px-4 py-6 sm:px-6 space-y-5 md:space-y-6">
        <header className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="font-[Instrument_Serif] text-2xl sm:text-[28px] leading-tight text-[hsl(var(--foreground))] m-0">
              {t("globalLibrary.title")}
            </h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 text-neutral-500 dark:text-neutral-400">
              {t("globalLibrary.deckCount", { count: totalDecks })}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
          <div className="flex-1 min-w-0 max-w-md">
            <div className="flex items-center gap-3 py-2.5 px-4 rounded-xl bg-[hsl(var(--bg3))] border border-[hsl(var(--border))]">
              <Search size={20} className="shrink-0 text-[hsl(var(--muted-foreground))]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("globalLibrary.searchPlaceholder")}
                className="flex-1 min-w-0 bg-transparent text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none text-sm"
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
            <div className="flex flex-wrap gap-2 items-center">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="h-9 sm:h-10 rounded-xl border border-[hsl(var(--border))] px-3 sm:px-4 text-sm font-medium bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--border))]/10 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40 focus:border-[hsl(var(--primary))]/50 transition-colors"
              >
                <option value="recent">{t("home.sortRecent")}</option>
                <option value="alphabetical">{t("home.sortAlphabetical")}</option>
                <option value="language">{t("home.sortByLanguage")}</option>
              </select>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as DateFilterOption)}
                className="h-9 sm:h-10 rounded-xl border border-[hsl(var(--border))] px-3 sm:px-4 text-sm font-medium bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--border))]/10 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40 focus:border-[hsl(var(--primary))]/50 transition-colors"
                title={t("globalLibrary.filterDateToday")}
              >
                <option value="all">{t("globalLibrary.filterDateAll")}</option>
                <option value="today">{t("globalLibrary.filterDateToday")}</option>
              </select>
              <select
                value={activeLanguage ?? ""}
                onChange={(e) => setActiveLanguage(e.target.value || null)}
                className="h-9 sm:h-10 rounded-xl border border-[hsl(var(--border))] px-3 sm:px-4 text-sm font-medium bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--border))]/10 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40 focus:border-[hsl(var(--primary))]/50 transition-colors"
              >
                <option value="">{t("home.filterAllLanguages")}</option>
                {uniqueLanguages.map((l) => (
                  <option key={l.lang} value={l.lang}>{l.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              {(["cards", "learn", "test", "match", "philology"] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTypeFilter(activeTypeFilter === tab ? null : tab)}
                  className={`h-9 sm:h-10 inline-flex items-center justify-center gap-2 rounded-xl border px-3 sm:px-4 text-sm font-medium transition-all duration-150 ${
                    activeTypeFilter === tab
                      ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))] shadow-sm active:scale-[0.98]"
                      : "bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:bg-[hsl(var(--border))]/10 active:scale-[0.98]"
                  }`}
                >
                  {tab === "match" ? t("home.tabsGames") : t(`deckModal.studyMode${tab.charAt(0).toUpperCase() + tab.slice(1)}`)}
                </button>
              ))}
            </div>
          </div>
          </div>
        </header>

        {dateFilter === "today" && (decks.length > 0 || totalDecks > 0) && (
          <div className="flex flex-wrap items-center gap-3 py-2.5 px-4 rounded-xl bg-[hsl(var(--card-bg))] border border-[hsl(var(--border))]">
            <span className="text-sm text-[hsl(var(--muted-foreground))]">
              {t("globalLibrary.todayActionsHint")}: {totalDecks}
              {hasMoreDecks && ` (${t("globalLibrary.loadedOf", { loaded: decks.length, total: totalDecks })})`}
            </span>
            <div className="flex flex-wrap items-center gap-2 ml-auto">
              <button
                type="button"
                onClick={handleAddAllTodayToLibrary}
                disabled={addAllToLibraryLoading}
                className="h-9 inline-flex items-center justify-center gap-2 rounded-xl border border-[hsl(var(--border))] px-3 sm:px-4 text-sm font-medium bg-[hsl(var(--primary))] text-white hover:opacity-90 disabled:opacity-60 transition-opacity"
              >
                <Library size={16} />
                {addAllToLibraryLoading
                  ? addAllToLibraryProgress && addAllToLibraryProgress.total > 0
                    ? t("globalLibrary.addAllToLibraryProgress", {
                        percent: Math.round((addAllToLibraryProgress.current / addAllToLibraryProgress.total) * 100),
                      })
                    : "…"
                  : t("globalLibrary.addAllToLibrary")}
              </button>
              <button
                type="button"
                onClick={openAddToFolderModal}
                className="h-9 inline-flex items-center justify-center gap-2 rounded-xl border border-[hsl(var(--border))] px-3 sm:px-4 text-sm font-medium bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--border))]/10 transition-colors"
              >
                <FolderPlus size={16} />
                {t("globalLibrary.addToFolder")}
              </button>
            </div>
          </div>
        )}

        {decks.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: 60,
              marginTop: 24,
              background: "hsl(var(--card-bg))",
              border: "2px dashed hsl(var(--border))",
              borderRadius: "var(--radius)",
              color: "hsl(var(--muted-foreground))",
            }}
          >
            <Inbox size={48} className="mx-auto mb-4" />
            {t("globalLibrary.empty")}
          </div>
        ) : (
          <div className="mt-8">
            {(["today", "this_week", "older"] as const).map((groupKey) => {
              const items = groupedDecks[groupKey];
              if (items.length === 0) return null;
              const labels: Record<TimeGroup, string> = {
                today: t("home.groupToday"),
                this_week: t("home.groupThisWeek"),
                older: t("home.groupOlder"),
              };
              return (
                <section key={groupKey} className="mb-8">
                  <h3
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      color: "hsl(var(--muted-foreground))",
                      marginBottom: 12,
                    }}
                  >
                    {labels[groupKey]}
                  </h3>
                  <div
                    className="grid gap-3 md:gap-4 w-full"
                    style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(260px, 100%), 1fr))" }}
                  >
                    {items.map((deck) => (
                      <div
                        key={deck.id}
                        className="relative flex flex-col gap-2.5 md:gap-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-3.5 md:p-5 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-neutral-300 dark:hover:border-neutral-600 transition-all duration-200 ease-out cursor-pointer"
                      >
                        <div
                          className="absolute inset-x-0 top-0 h-[2px] rounded-t-xl"
                          style={{ background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent2)))" }}
                        />
                        <div className="flex flex-col gap-1.5 md:gap-2">
                          <div className="flex items-center gap-2 md:gap-3 min-w-0">
                            <div className="flex justify-center items-center w-9 h-9 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-[hsl(var(--primary)/.12)] text-[hsl(var(--primary))] shrink-0">
                              <BookOpen size={18} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-base font-semibold text-[hsl(var(--foreground))] leading-snug truncate">
                                {deck.title}
                              </h3>
                              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                                {t("sidebar.cardsCount", { count: deck.cardCount ?? deck.cards?.length ?? 0 })}
                              </p>
                            </div>
                          </div>
                          <span className="inline-flex items-center gap-1 self-start rounded-md bg-[hsl(var(--bg3))] px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                            <Globe size={10} className="shrink-0 opacity-80" />
                            {deck.lang.toUpperCase()}
                            {deck.level && ` · ${deck.level}`}
                          </span>
                        </div>
                        <div className="flex flex-col gap-2.5 md:gap-3 mt-auto pt-0">
                          <div className="flex flex-col gap-2 md:gap-3">
                            {(() => {
                              const filter = activeTypeFilter ?? "cards";
                              const mode = filterToLaunchMode(filter);
                              const effectiveMode: DeckLaunchMode = mode;
                              const primaryLabel = getDeckLaunchLabel(
                                t,
                                effectiveMode,
                                deck.type === "tests" ? "tests" : undefined
                              );
                              const isLaunching = launchingDeckId === deck.id;
                              const PrimaryIcon =
                                effectiveMode === "games"
                                  ? Gamepad2
                                  : effectiveMode === "test"
                                    ? ClipboardList
                                    : effectiveMode === "learn"
                                      ? GraduationCap
                                      : SquareStack;
                              const cardCount = deck.cardCount ?? deck.cards?.length ?? 0;
                              const canStudy = cardCount > 0;
                              return (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => canStudy && handleLaunchStudy(deck, effectiveMode)}
                                    disabled={!canStudy || isLaunching}
                                    className={`inline-flex items-center justify-center gap-2 h-11 px-4 md:px-5 rounded-xl md:rounded-2xl border-0 text-white text-sm font-semibold shadow-sm transition-all duration-200 ease-out hover:opacity-95 hover:shadow-md hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50 disabled:hover:scale-100 ${
                                      effectiveMode === "games"
                                        ? "bg-gradient-to-r from-orange-500 to-orange-400 hover:from-orange-600 hover:to-orange-500 border-transparent text-white"
                                        : "bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))]"
                                    }`}
                                  >
                                    <PrimaryIcon size={16} className="shrink-0" />
                                    {isLaunching ? t("library.loading") : primaryLabel}
                                  </button>
                                  {canStudy && (
                                    <button
                                      type="button"
                                      onClick={() => handleLaunchStudy(deck, effectiveMode, true)}
                                      disabled={isLaunching}
                                      className="inline-flex items-center justify-center gap-2 h-10 md:h-11 px-4 md:px-5 rounded-xl md:rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--muted-foreground))] text-sm font-medium transition-all duration-150 hover:bg-[hsl(var(--bg3))]/80 hover:text-[hsl(var(--foreground))] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <ChevronRight size={16} />
                                      {t("home.goToModule")}
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          <button
                            onClick={() => handleAddToLibrary(deck)}
                            disabled={savingDeckId === deck.id}
                            className="inline-flex items-center justify-center gap-2 h-10 w-full px-4 rounded-xl md:rounded-2xl border border-emerald-500/20 bg-emerald-500/10 text-emerald-600 text-sm font-medium cursor-pointer hover:bg-emerald-500/15 active:scale-[0.98] disabled:opacity-60 disabled:cursor-wait dark:text-emerald-400 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/15"
                          >
                            <Download size={16} className="shrink-0" />
                            {savingDeckId === deck.id ? t("library.loading") : t("globalLibrary.addToLibrary")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}

            {hasMoreDecks && (
              <div className="mt-6 flex flex-col items-center gap-3">
                <div ref={loadMoreSentinelRef} className="h-1 w-full min-h-[1px]" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] px-6 py-3 text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--bg3))]/80 disabled:opacity-60 disabled:cursor-wait"
                >
                  {loadingMore ? (
                    <>
                      <span className="h-4 w-4 rounded-full border-2 border-[hsl(var(--border))] border-t-[hsl(var(--primary))] animate-spin" aria-hidden />
                      {t("library.loading")}
                    </>
                  ) : (
                    t("common.showMore")
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        <Dialog open={addToFolderModalOpen} onOpenChange={(open) => { if (!addToFolderLoading) setAddToFolderModalOpen(open); }}>
          <DialogContent className="sm:max-w-md" aria-describedby={addToFolderLoading ? "add-to-folder-progress-desc" : undefined}>
            <DialogHeader>
              <DialogTitle>{t("globalLibrary.addToFolderModalTitle")}</DialogTitle>
            </DialogHeader>
            {addToFolderLoading && addToFolderProgress ? (
              <div id="add-to-folder-progress-desc" className="space-y-3 py-2">
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  {addToFolderProgress.phase === "fetching" && t("globalLibrary.addToFolderProgressFetching")}
                  {addToFolderProgress.phase === "copying" && t("globalLibrary.addToFolderProgressCopying", { current: addToFolderProgress.current, total: addToFolderProgress.total })}
                  {addToFolderProgress.phase === "adding" && t("globalLibrary.addToFolderProgressAdding")}
                </p>
                <div className="h-2 w-full rounded-full bg-[hsl(var(--border))] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[hsl(var(--primary))] transition-[width] duration-300"
                    style={{
                      width: addToFolderProgress.total
                        ? `${Math.round((addToFolderProgress.current / addToFolderProgress.total) * 100)}%`
                        : "30%",
                    }}
                  />
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">{t("globalLibrary.addToFolderProgressHint")}</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  {t("globalLibrary.addToFolderModalHint")}
                </p>
                <div className="py-2">
                  {foldersForModal.length === 0 ? (
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t("folder.noFolders")}</p>
                  ) : (
                    <select
                      value={selectedFolderIdForBulk}
                      onChange={(e) => setSelectedFolderIdForBulk(e.target.value)}
                      className="w-full h-10 rounded-xl border border-[hsl(var(--border))] px-3 text-sm bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
                    >
                      {foldersForModal.map((f) => (
                        <option key={f.id} value={f.id}>{f.title}</option>
                      ))}
                    </select>
                  )}
                </div>
              </>
            )}
            <DialogFooter>
              <button
                type="button"
                onClick={() => setAddToFolderModalOpen(false)}
                className="h-9 px-4 rounded-xl border border-[hsl(var(--border))] text-sm font-medium bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--border))]/10"
              >
                {t("shared.close")}
              </button>
              <button
                type="button"
                onClick={handleAddToFolderConfirm}
                disabled={addToFolderLoading || foldersForModal.length === 0 || !selectedFolderIdForBulk}
                className="h-9 px-4 rounded-xl border-0 text-sm font-medium bg-[hsl(var(--primary))] text-white hover:opacity-90 disabled:opacity-60"
              >
                {addToFolderLoading ? "…" : t("globalLibrary.addToFolder")}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
